import { tokenUtils } from "../utils/tokenUtils";
import { normalizeLocalUser } from "../utils/normalizeLocalUser";

const API_BASE_URL = process.env.REACT_APP_API_URL_LARAVEL || 'http://localhost:8000/api';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5116/api';

const ENDPOINTS = {
  LOGIN: '/mega-academia/login',
  USER: '/mega-academia/user',
};

const ERROR_MESSAGES = {
  INVALID_CREDENTIALS: 'Credenciales inválidas',
  UNAUTHORIZED: 'No autorizado',
  NETWORK_ERROR: 'Error de conexión',
  TOKEN_EXPIRED: 'Sesión expirada',
  UNKNOWN_ERROR: 'Error desconocido',
  MISSING_CEDULA: 'Tu cuenta no tiene cédula asociada. Contacta a soporte para completar el registro.',
  LOCAL_USER_FAILED: 'No se pudo registrar tu usuario en el sistema local.',
  LOCAL_API_UNREACHABLE: 'No se pudo conectar con el servidor. Verifica tu conexión y que los servicios estén en ejecución.',
};

const parseJSON = async (response) => {
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    try {
      return await response.json();
    } catch (error) {
      console.error('Error al parsear JSON:', error);
      return {};
    }
  }
  return {};
};

const decodeJwtPayload = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch (e) {
    console.error('Error decodificando JWT:', e);
    return null;
  }
};

const getApiErrorMessage = (errorData, fallback) => {
  if (!errorData || typeof errorData !== 'object') return fallback;
  return (
    errorData.message ||
    errorData.mensaje ||
    errorData.title ||
    errorData.detail ||
    (typeof errorData.error === 'string' ? errorData.error : null) ||
    fallback
  );
};

const toNetworkError = (error, fallback) => {
  const msg = error?.message || '';
  if (
    msg.includes('Failed to fetch') ||
    msg.includes('NetworkError') ||
    msg.includes('Load failed')
  ) {
    return new Error(ERROR_MESSAGES.LOCAL_API_UNREACHABLE);
  }
  return error instanceof Error ? error : new Error(fallback);
};

/**
 * Garantiza el usuario en la base interna (.NET).
 * GET por cédula; si no existe, create-from-token (upsert).
 * Completa profile/label cuando la API solo manda labelId.
 */
const ROLE_FALLBACK = {
  1: 'Estudiante',
  2: 'Admin',
  3: 'Profesor',
};

const enrichLocalUserProfile = async (token, user) => {
  const normalized = normalizeLocalUser(user);
  if (!normalized?.id) return normalized;

  const roleId = normalized.roleId != null ? Number(normalized.roleId) : null;
  if (!normalized.role?.name && roleId && ROLE_FALLBACK[roleId]) {
    normalized.role = {
      ...(normalized.role || {}),
      id: roleId,
      name: ROLE_FALLBACK[roleId],
    };
  }

  if (normalized.profile || normalized.label?.name) {
    if (!normalized.profile && normalized.label?.name) {
      normalized.profile = normalized.label.name;
    }
    return normalized;
  }

  const labelId = normalized.labelId;
  if (!labelId) {
    // Buscar etiqueta de perfil en user_labels + catálogo de labels
    try {
      const [labelsRes, userLabelsRes] = await Promise.all([
        fetch(`${API_URL}/Label`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/UserLabel/user/${normalized.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (labelsRes.ok && userLabelsRes.ok) {
        const allLabels = await parseJSON(labelsRes);
        const userLabels = await parseJSON(userLabelsRes);
        const labelList = Array.isArray(allLabels) ? allLabels : [];
        const assignedIds = new Set(
          (Array.isArray(userLabels) ? userLabels : []).map(
            (ul) => ul.labelId ?? ul.LabelId
          )
        );
        const profileLabel = labelList.find(
          (l) =>
            assignedIds.has(l.id ?? l.Id) &&
            Boolean(l.isProfile ?? l.IsProfile)
        );
        if (profileLabel) {
          const name = profileLabel.name ?? profileLabel.Name;
          const id = profileLabel.id ?? profileLabel.Id;
          normalized.profile = name;
          normalized.labelId = id;
          normalized.label = { id, name, isProfile: true };
          return normalized;
        }
      }
    } catch (e) {
      console.warn('No se pudieron leer user_labels para el perfil:', e);
    }
    return normalized;
  }

  try {
    const labelRes = await fetch(`${API_URL}/Label/${labelId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (labelRes.ok) {
      const labelData = await parseJSON(labelRes);
      const name = labelData?.name || labelData?.Name;
      if (name) {
        normalized.profile = name;
        normalized.label = {
          id: labelData.id ?? labelData.Id ?? labelId,
          name,
          isProfile: Boolean(labelData.isProfile ?? labelData.IsProfile),
        };
      }
    }
  } catch (e) {
    console.warn('No se pudo resolver Label por id:', e);
  }

  return normalized;
};

const ensureLocalUser = async (token, userPayload) => {
  const cedula = (
    userPayload?.cedula?.toString?.()?.trim?.() ||
    userPayload?.Cedula?.toString?.()?.trim?.() ||
    ''
  );
  if (!cedula) {
    throw new Error(ERROR_MESSAGES.MISSING_CEDULA);
  }

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  try {
    const cedulaResponse = await fetch(`${API_URL}/User/cedula/${encodeURIComponent(cedula)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (cedulaResponse.ok) {
      const existing = normalizeLocalUser(await parseJSON(cedulaResponse));
      if (existing?.id) {
        const enriched = await enrichLocalUserProfile(token, existing);
        return { ...enriched, isNewLocalUser: false };
      }
    }

    console.log('Usuario local no encontrado, creando/reactivando...');
    const createUserRes = await fetch(`${API_URL}/User/create-from-token`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        name: userPayload?.name || userPayload?.Name || userPayload?.user || 'Usuario',
        user: userPayload?.user || userPayload?.name || userPayload?.Name || 'Usuario',
        cedula,
        email: userPayload?.email || userPayload?.Email || null,
      }),
    });

    const createData = await parseJSON(createUserRes);
    if (!createUserRes.ok) {
      if (createUserRes.status === 401) {
        throw new Error(
          'No se pudo validar tu sesión contra el sistema local (token JWT). Verifica la configuración del secret.'
        );
      }
      throw new Error(getApiErrorMessage(createData, ERROR_MESSAGES.LOCAL_USER_FAILED));
    }

    const normalized = normalizeLocalUser(createData);
    if (!normalized?.id) {
      throw new Error(ERROR_MESSAGES.LOCAL_USER_FAILED);
    }

    const enriched = await enrichLocalUserProfile(token, normalized);
    return { ...enriched, isNewLocalUser: true };
  } catch (error) {
    throw toNetworkError(error, ERROR_MESSAGES.LOCAL_USER_FAILED);
  }
};

export const authService = {
  ensureLocalUser,

  login: async (email, password) => {
    try {
      console.log('Iniciando proceso de login para:', email);

      // Login vía API .NET: valida en Laravel y emite JWT largo (sin exp corta de 1h)
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await parseJSON(response);
      console.log('Respuesta del servidor:', data);

      if (!response.ok) {
        throw new Error(data.message || ERROR_MESSAGES.INVALID_CREDENTIALS);
      }

      if (!data.token) {
        throw new Error('No se recibió token de autenticación');
      }

      const laravelUser = data.user || data.User || null;
      const localFromLogin = normalizeLocalUser(data.localUser || data.LocalUser);

      if (!laravelUser && !localFromLogin) {
        throw new Error('No se recibieron datos del usuario');
      }

      const sessionUser = {
        ...(typeof laravelUser === 'object' && laravelUser ? laravelUser : {}),
        cedula: laravelUser?.cedula || localFromLogin?.cedula,
        name: laravelUser?.name || laravelUser?.user || localFromLogin?.name,
        email: laravelUser?.email || localFromLogin?.email || email,
        role: laravelUser?.rol || laravelUser?.role || localFromLogin?.role?.name,
      };

      tokenUtils.set(data.token);
      tokenUtils.setUser(sessionUser);

      const storedToken = tokenUtils.get();
      const storedUser = tokenUtils.getUser();

      if (!storedToken) {
        throw new Error('Error al guardar el token de autenticación');
      }

      if (!storedUser) {
        throw new Error('Error al guardar los datos del usuario');
      }

      console.log('Token y usuario guardados correctamente');

      let normalizedLocal = localFromLogin;
      let isNewLocalUser = false;
      if (!normalizedLocal?.id) {
        const usuarioConCedula = await ensureLocalUser(data.token, sessionUser);
        isNewLocalUser = Boolean(usuarioConCedula?.isNewLocalUser);
        normalizedLocal = normalizeLocalUser(usuarioConCedula);
      }

      if (!normalizedLocal?.id) {
        throw new Error(ERROR_MESSAGES.LOCAL_USER_FAILED);
      }

      localStorage.setItem('usuarioConCedula', JSON.stringify(normalizedLocal));
      console.log('Usuario local sincronizado:', normalizedLocal);

      return { ...data, user: sessionUser, localUser: normalizedLocal, isNewLocalUser };
    } catch (error) {
      console.error('Error en login:', error);
      tokenUtils.clearAuth();
      throw toNetworkError(error, ERROR_MESSAGES.UNKNOWN_ERROR);
    }
  },

  logout: () => {
    console.log('Cerrando sesión...');
    tokenUtils.clearAuth();
  },

  getAuthHeaders: () => {
    const token = tokenUtils.get();
    if (!token) {
      console.error('No hay token disponible');
      throw new Error('No hay token disponible');
    }
    
    if (tokenUtils.isExpired(token)) {
      console.error('Token expirado');
      authService.logout();
      throw new Error(ERROR_MESSAGES.TOKEN_EXPIRED);
    }
    
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  },

  verifyCedula: async (cedula) => {
    try {
      const response = await fetch(`${API_URL}/auth/habeas-data/verify-cedula`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cedula }),
      });
      if (!response.ok) {
        const data = await parseJSON(response);
        throw new Error(data.message || 'Error al verificar la cédula');
      }
    } catch (error) {
      throw toNetworkError(error, 'Error al verificar la cédula');
    }
  },

  loginDoctorByCedula: async (cedula, otp) => {
    try {
      const response = await fetch(`${API_URL}/auth/habeas-data/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cedula, otp }),
      });

      const data = await parseJSON(response);
      if (!response.ok) {
        throw new Error(data.message || 'Error al verificar el OTP');
      }
      if (!data.token) {
        throw new Error('No se recibió token de autenticación');
      }

      const payload = decodeJwtPayload(data.token);
      if (!payload) {
        throw new Error('Token inválido');
      }
      const user = {
        cedula: payload.cedula,
        name: payload.user,
        user: payload.user,
        email: payload.email,
        rol: payload.role,
        role: payload.role,
        authSource: 'habeas-data', // No usar Laravel /api/mega-academia/user
      };

      tokenUtils.set(data.token);
      tokenUtils.setUser(user);

      const usuarioConCedula = await ensureLocalUser(data.token, user);
      const normalizedLocal = normalizeLocalUser(usuarioConCedula);
      localStorage.setItem('usuarioConCedula', JSON.stringify(normalizedLocal));

      return {
        token: data.token,
        user,
        localUser: normalizedLocal,
        isNewLocalUser: Boolean(usuarioConCedula?.isNewLocalUser),
      };
    } catch (error) {
      console.error('Error en login doctor:', error);
      tokenUtils.clearAuth();
      throw toNetworkError(error, 'Error al verificar el OTP');
    }
  },

  updateMyEmail: async (email) => {
    const response = await fetch(`${API_URL}/User/me/email`, {
      method: 'PUT',
      headers: authService.getAuthHeaders(),
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const data = await parseJSON(response);
      throw new Error(
        data.message || data.title || data.detail || 'No se pudo actualizar el correo'
      );
    }

    if (response.status === 204) {
      return null;
    }
    return parseJSON(response);
  },

  /**
   * Prioridad para el email en .NET: primero GET /User/{id}, luego GET /User/me.
   * Devuelve null ante 401/403/404 o sin token en ambos intentos.
   */
  fetchNetUserProfile: async (dotnetUserId) => {
    let headers;
    try {
      headers = authService.getAuthHeaders();
    } catch {
      return null;
    }

    const readIfOk = async (response) => {
      if (!response.ok) return null;
      return parseJSON(response);
    };

    try {
      if (dotnetUserId != null && dotnetUserId !== '') {
        const byIdRes = await fetch(`${API_URL}/User/${dotnetUserId}`, { headers });
        const byIdUser = await readIfOk(byIdRes);
        if (byIdUser) return byIdUser;
      }

      const meRes = await fetch(`${API_URL}/User/me`, { headers });
      return await readIfOk(meRes);
    } catch {
      return null;
    }
  },

  fetchUser: async () => {
    try {
      console.log('Verificando usuario en servidor...');
      
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.USER}`, {
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error('Usuario no autorizado (401)');
          // No limpiar aquí: AuthProvider decide si conservar sesión local
          throw new Error(ERROR_MESSAGES.UNAUTHORIZED);
        }
        throw new Error(`Error del servidor: ${response.status}`);
      }

      const userData = await response.json();
      console.log('Usuario verificado en servidor:', userData);
      
      return { ...userData, roles: userData.roles || [userData.rol] };
    } catch (error) {
      console.error('Error al verificar usuario:', error);
      throw toNetworkError(error, ERROR_MESSAGES.UNKNOWN_ERROR);
    }
  },
};
